import { MutableRefObject, useContext, useEffect, useState } from 'react';
import ApplicationContext from '../../../ApplicationContext';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import { JobNames, getTbNameByJobName } from '../../../framework.core/JobNames';
import SecondaryButton from '../../../ui/SecondaryButton';
import Label from '../../../ui/Label';
import InlineInputControlRow from '../../../ui/layout/InlineInputControlRow';
import TextBox from '../../../ui/TextBox';
import { ApiResult } from '../../../framework.core/data/ApiResult';
import { InvoHd } from '../../../fms.core/InvoHd';
import { getAcctHeadTypeByTBName } from '../../../fms.enum';
import { useTypedFastStorage } from '../../../hooks/useFastStorage';
import CopyInvTargetBLLookupField from '../../../uiControls/lookup/CopyInvTargetBLLookupField';
import { openNewPage, NavigationType } from '../../../util/navigationHelpers';
import { useHistory } from 'react-router';
import useJobMenuUrlQuery from '../../../hooks/useJobMenuUrlQuery';
import CopyInvTargetAcctLookupField from '../../../uiControls/lookup/CopyInvTargetAcctLookupField';


interface ICopyInvoice {
    [key: string]: any,
    fieldLabel: FieldLabel,
    baseInfoDetail: BaseInfoDetail
};

interface FieldLabel {
    blType: string,
    mblName: string,
    hblName: string,
    refName: string,
};

interface BaseInfoDetail {
    refNo: string,
    mblNo: string,
    hblNo: string,
    targetId: number,
    invNo: string,
    jobId: number,
    scrJobId: number,
};

interface CopyInoviceStorageKeys {
    modelStorage: string,
};

function getCopyInoviceStorageKeys(): CopyInoviceStorageKeys {
    return {modelStorage: 'common-copyinvoice'}
}


function CopyInvoiceImpl({
  jobName,
  jobId,
  srcJobId,
  srcJobName,
  onPopupClose,
  isFromCopy,
  isOtherJob,
}: {
  jobName: JobNames,
  jobId: number,
  srcJobName: JobNames,
  srcJobId: number,
  onPopupClose: () => void,
  isFromCopy? :boolean,
  isOtherJob? : boolean,
}) {

  const {modelStorage} = getCopyInoviceStorageKeys();

  const appContext = useContext(ApplicationContext);

  const [showGlobalOkDialog] = useGlobalOkDialog();

  const [initialized, setInitialized] = useState<boolean>(false);

  const [inStorage, setInStorage, removeCopyInvoiceFromStorage, copyInvoiceRef] = useTypedFastStorage<ICopyInvoice>(modelStorage, null);

  useEffect(() => {

    initialize();
    return () => removeCopyInvoiceFromStorage()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  async function readBaseInfo(){

    const hdTbName = getTbNameByJobName(srcJobName);
    const hdOpType = getAcctHeadTypeByTBName(hdTbName);
    const baseInfoDetail : BaseInfoDetail = {refNo: '', mblNo: '', hblNo: '', targetId: 0, invNo : '', jobId:0, scrJobId:0};
    const apiResult: ApiResult<InvoHd> = await appContext.api.getBLHDInfo(hdOpType, srcJobId, '');
    if(apiResult && apiResult.result && apiResult.result.blHdInfo){
        baseInfoDetail.refNo = apiResult.result.blHdInfo.mainRefNo;
        baseInfoDetail.mblNo = apiResult.result.blHdInfo.mainMBLNo;
        baseInfoDetail.hblNo = apiResult.result.blHdInfo.mainHBLNo;
        baseInfoDetail.targetId = srcJobId;
        setInStorage({baseInfoDetail: baseInfoDetail});
    }
    return baseInfoDetail;
}

  async function initialize(){
    if(!inStorage){
       const fieldLabel = getPopupLabel(srcJobName);
       const baseInfoDetail =  await readBaseInfo();
       setInStorage({fieldLabel: fieldLabel, baseInfoDetail: baseInfoDetail})
    }
    setInitialized(true);
}


if (!initialized || !inStorage) {
    return null;
  }

  function getPopupLabel(srcJobName: string) {
    const fieldLabel : FieldLabel = {blType: '', mblName: '', hblName: '', refName: 'Ref. No.'} ;
    switch (srcJobName) {
        case JobNames.OIMbl:
            fieldLabel.blType = "Ocean Import Master";
            fieldLabel.mblName = "Master B/L #";
            break;
            case JobNames.OIHbl:    
            fieldLabel.blType = "Ocean Import House";
            fieldLabel.mblName = "Master B/L #";
            fieldLabel.hblName = "House B/L #";
            break;
        case JobNames.OEMbl:    
            fieldLabel.blType = "Ocean Export Master";
            fieldLabel.mblName = "Master B/L #";
            break;
        case JobNames.OEHbl:    
            fieldLabel.blType = "Ocean Export House";
            fieldLabel.mblName = "Master B/L #";
            fieldLabel.hblName = "House B/L #";
            break;
        case JobNames.AIMbl:    
            fieldLabel.blType = "Air Import Master";
            fieldLabel.mblName = "MAWB #";
            break;
        case JobNames.AIHbl:    
            fieldLabel.blType = "Air Import House";
            fieldLabel.mblName = "MAWB #";
            fieldLabel.hblName = "HAWB #";
            break;
        case JobNames.AEMbl:    
            fieldLabel.blType = "Air Export Master";
            fieldLabel.mblName = "MAWB #";
            break;
        case JobNames.AEHbl:    
            fieldLabel.blType = "Air Export House";
            fieldLabel.mblName = "MAWB #";
            fieldLabel.hblName = "HAWB #";
            break;
        case JobNames.OtherOP:  
            fieldLabel.blType = "Other Shipment";
            fieldLabel.mblName = "Master B/L #";
            fieldLabel.hblName = "House B/L #";
            break;    
        case JobNames.BTOrder:  
            fieldLabel.blType = "Manifest";
            fieldLabel.refName = "PRO #";
            break;    
        case JobNames.BTLoadPlan:  
            fieldLabel.blType = "Load";
            fieldLabel.refName = "Load #";
            break;    
        case JobNames.BTDo:
            fieldLabel.blType = "Manifest";
            fieldLabel.refName = "PRO #";
            break;    
    }
    return fieldLabel;
  }

  return (
    <CopyInvoiceInitializedImpl
        copyInvoice={inStorage}
        copyInvoiceRef={copyInvoiceRef}
        setCopyInvoice={setInStorage}
        onPopupClose={onPopupClose}
        srcJobName={srcJobName}
        jobName={jobName}
        srcJobId={srcJobId}
        jobId={jobId}
        isFromCopy={isFromCopy}
        isOtherJob={isOtherJob}
    />
  );

}

function CopyInvoiceInitializedImpl({
    copyInvoice,
    copyInvoiceRef,
    setCopyInvoice,
    onPopupClose,
    srcJobName,
    jobName,
    srcJobId,
    jobId,
    isFromCopy,
    isOtherJob,
}:{
    copyInvoice: ICopyInvoice,
    copyInvoiceRef: MutableRefObject<ICopyInvoice|null>,
    setCopyInvoice: (x: Partial<ICopyInvoice>) =>void,
    onPopupClose: () => void,
    srcJobName : JobNames,
    jobName : JobNames,
    srcJobId: number,
    jobId: number,
    isFromCopy?: boolean
    isOtherJob?: boolean,
}){

    const hasOpenedScreenStartingWithUrl = useJobMenuUrlQuery();
    const [showGlobalOkDialog] = useGlobalOkDialog();
    const history = useHistory();

    function onChange(change: Partial<ICopyInvoice>){
        setCopyInvoice(change);
    }

    async function onCopy() {
        const job = jobName === JobNames.Invoice ? 'invoice' : 'bill';
        if (isFromCopy) {
            if (copyInvoice.baseInfoDetail.jobId === 0) {
                onPopupClose();
                return;
            }
        }
        else {
            if (copyInvoice.baseInfoDetail.targetId === 0) {
                onPopupClose();
                return;
            }
        }

        if (hasOpenedScreenStartingWithUrl(`/accounting/${job}/add/${srcJobName}`)) {
            await showGlobalOkDialog(<p>New operation is already in progress</p>);
            return;
        }
        

        const newLink = isFromCopy ? `/accounting/${job}/add/${srcJobName}/${srcJobId}?copyId=${copyInvoice.baseInfoDetail.jobId}${isOtherJob? "&DO=true":""}`: 
        `/accounting/${job}/add/${srcJobName}/${copyInvoice.baseInfoDetail.targetId}?copyId=${jobId}${isOtherJob? "&DO=true":""}`;

        const locationState = history.location?.state as any;
        if (locationState?.afterScreenUrl) {
            openNewPage(history, newLink, NavigationType.newPage, null, locationState?.afterScreenUrl); // From Detail
        }
        else {
            const parentOp = history.location.search?.split("parentOp=")[1];
            openNewPage(history, newLink, NavigationType.newPage, null, parentOp ? parentOp : null);    // From List
        }

    }

    return (
        <div className='report-popup'>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', fontWeight: 'bold' }}>
                <div style={{ textAlign: 'center', paddingBottom: '10px' }}>
                    <InlineInputControlRow smallMargin mode="w60" label="Type: ">
                        <Label label={copyInvoice.fieldLabel.blType ?? ''} style={{ fontSize: '14px' }} />
                    </InlineInputControlRow>
                </div>
                <div style={{ textAlign: 'center', paddingBottom: '10px' }}>
                    {!isFromCopy && <InlineInputControlRow smallMargin mode="small" label={`Target ${copyInvoice.fieldLabel.refName}`}>
                        <CopyInvTargetBLLookupField
                            lookupId="invoice-copy"
                            initialValue={copyInvoice.baseInfoDetail.refNo ?? ''}
                            onSelect={c => {
                                const tempbaseInfo = copyInvoice.baseInfoDetail;
                                tempbaseInfo.refNo = c.refNo;
                                tempbaseInfo.mblNo = c.mblNo;
                                tempbaseInfo.hblNo = c.hblNo;
                                tempbaseInfo.targetId = c.id;
                                onChange({ baseInfoDetail: tempbaseInfo })
                            }}
                            onTextChange={c => {
                                const tempbaseInfo = copyInvoice.baseInfoDetail;
                                tempbaseInfo.refNo = c ?? '';
                                tempbaseInfo.targetId = 0;
                                onChange({ baseInfoDetail: tempbaseInfo })
                            }}
                            placeholder={undefined}
                            jobType={srcJobName}
                        />
                    </InlineInputControlRow>}
                    {isFromCopy && <InlineInputControlRow smallMargin mode="small" label={jobName === JobNames.Invoice ? 'Target Inv #' : 'Target Bill #'}>
                        <CopyInvTargetAcctLookupField
                            lookupId="invoice-copy-Acct"
                            initialValue={copyInvoice.baseInfoDetail.invNo ?? ''}
                            onSelect={c => {
                                const tempbaseInfo = copyInvoice.baseInfoDetail;
                                tempbaseInfo.invNo = c.invNo;
                                tempbaseInfo.refNo = c.invNo;
                                tempbaseInfo.jobId = c.id;
                                onChange({ baseInfoDetail: tempbaseInfo })
                            }}
                            onTextChange={c => {
                                const tempbaseInfo = copyInvoice.baseInfoDetail;
                                tempbaseInfo.invNo = c ?? '';
                                tempbaseInfo.scrJobId = 0;
                                tempbaseInfo.jobId = 0;
                                onChange({ baseInfoDetail: tempbaseInfo })
                            }}
                            placeholder={undefined}
                            jobType={jobName}
                            parent={srcJobName === JobNames.BTDo ? JobNames.BTOrder : srcJobName}
                        />
                    </InlineInputControlRow>}
                </div>
                {!isFromCopy && [JobNames.BTOrder, JobNames.BTLoadPlan].indexOf(srcJobName) < 0 &&
                    <div style={{ textAlign: 'center', paddingBottom: '10px' }}>
                        <InlineInputControlRow smallMargin mode="small" label={copyInvoice?.fieldLabel.mblName}>
                            <TextBox noborder disabled value={copyInvoice.baseInfoDetail.mblNo ?? ''} />
                        </InlineInputControlRow>
                    </div>
                }
                {!isFromCopy && copyInvoice.fieldLabel.hblName && copyInvoice.fieldLabel.hblName.length > 0 && [JobNames.BTOrder, JobNames.BTLoadPlan].indexOf(srcJobName) < 0 &&
                    <div style={{ textAlign: 'center', paddingBottom: '10px' }}>
                        <InlineInputControlRow smallMargin mode="small" label={copyInvoice.fieldLabel.hblName}>
                            <TextBox noborder disabled value={copyInvoice.baseInfoDetail.hblNo ?? ''} />
                        </InlineInputControlRow>
                    </div>
                }
            </div>
            <div className='report-footer'>
                <SecondaryButton
                size="regular"
                style={{ width: '90px' }}
                onClick={onCopy}>
                OK
                </SecondaryButton>
                <SecondaryButton
                size="regular"
                style={{ width: '90px' }}
                onClick={onPopupClose}>
                CLOSE
                </SecondaryButton>
             </div>
        </div>
    );

}

const CopyInvoice = ({
  onPopupClose,
  jobId,
  jobName,
  srcJobName,
  srcJobId,
  isFromCopy,
  isOtherJob,
}: {
  onPopupClose: () => void,
  jobId: number,
  jobName: JobNames,
  srcJobId: number,
  srcJobName: JobNames,
  isFromCopy?: boolean
  isOtherJob?: boolean,
}) => {
  return (
    <CopyInvoiceImpl
      jobName={jobName}
      jobId={jobId}
      srcJobName={srcJobName}
      srcJobId={srcJobId}
      onPopupClose={onPopupClose}
      key={`CopyInvoice-details`}
      isFromCopy = {isFromCopy}
      isOtherJob = {isOtherJob}
    />
  );
};

export default CopyInvoice;